import { Logger } from '@frontend/logger';
import { PackageClient } from '@frontend/package/api';
import { CreatePackage, Package, PackageStateName, UpdatePackage } from '@frontend/package/types';
import { MobileWizardStateRecord, updateData } from '@frontend/wizard/mobile';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../redux/store';

/**
 * Package synchronization states:
 * - INIT: Package exists locally but not yet created on backend
 * - AHEAD: Local changes exist that haven't been synced to backend
 * - SYNCED: Local and backend are in sync
 */
export enum PackageSyncState {
    INIT = 'init',
    AHEAD = 'ahead',
    SYNCED = 'synced'
}

export interface UseWizardPackageOptions {
    wizardId?: string;
    store: MobileWizardStateRecord | null;

    /**
     * Auto-create package on mount if conditions are met
     * @default true
     */
    autoCreate?: boolean;

    /**
     * Auto-fetch package from URL params on mount
     * @default true
     */
    autoFetch?: boolean;

    /**
     * Callback fired when package is created
     */
    onCreate?: (packageData: Package) => void | Promise<void>;

    /**
     * Callback fired when leaving screen/unmounting
     * Return false to prevent default cleanup behavior
     */
    onReturn?: () => boolean | Promise<boolean>;

    /**
     * Additional query params to look for package ID
     * @default ['package_id']
     */
    packageIdParams?: string[];
}

export interface UseWizardPackageReturn {
    /** Current package data (merged local + backend) */
    package: Partial<Package> | null;
    remotePackage: Package | null;

    /** Loading state for async operations */
    isLoading: boolean;

    /** Current synchronization state */
    packageState: PackageSyncState;

    /**
     * Update package data locally (in Redux store)
     * Does not sync to backend immediately
     */
    localUpdatePackage: (data: Partial<Package>) => Promise<void>;

    /**
     * Create package on backend if in INIT state, has required data and doesn't exist yet
     * Update package on backend if in AHEAD state
     * Transitions state from INIT|AHEAD to SYNCED
     */
    syncPackage: (data?: Partial<Package>) => Promise<Package>;

    /**
     * Refresh package data from backend
     * Overwrites local changes
     */
    refresh: () => Promise<void>;

    clearData: () => void;
}

/**
 * Helper to check if store has required data for package creation
 */
const hasRequiredDataForCreation = (store: MobileWizardStateRecord): boolean => {
    const transaction = store.data?.transaction;
    return !!(transaction?.id && store.wizard.account_id);
};

/**
 * Hook for managing wizard package lifecycle and synchronization
 */
export const useWizardPackage = (options: UseWizardPackageOptions): UseWizardPackageReturn => {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const { state: locationState } = useLocation();

    const [isLoading, setIsLoading] = useState(false);
    const [packageState, setPackageState] = useState<PackageSyncState>(PackageSyncState.INIT);
    const [remotePackage, setRemotePackage] = useState<Package | null>(null);

    // Track if component is mounted to prevent state updates after unmount
    const cleanupExecutedRef = useRef(false);
    const isCreatingRef = useRef(false);

    const { wizardId, store, autoCreate = false, autoFetch = true, packageIdParams = ['package_id'] } = options;
    const localPackage = store?.data?.package as Package | undefined;

    useEffect(() => {
        if (wizardId && !localPackage && locationState?.package) {
            dispatch(
                updateData({
                    key: wizardId,
                    data: { package: locationState.package }
                })
            );
        }
    }, []);

    // Store ref to access latest store state
    const storeRef = useRef(store);
    useEffect(() => {
        storeRef.current = store;
    }, [store]);

    /**
     * Helper to wait for store update to be reflected
     */
    const waitForStoreUpdate = async (checkFn: () => boolean, maxAttempts = 50, delayMs = 10) => {
        for (let i = 0; i < maxAttempts; i++) {
            if (checkFn()) {
                return true;
            }
            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
        return false;
    };

    /**
     * Initialize package from URL params or wizard store
     */
    useEffect(() => {
        if (!autoFetch || !store || !store.wizard.account_id || !wizardId) return;

        const initializePackage = async () => {
            const transaction = store.data?.transaction;
            if (!transaction?.id) {
                setPackageState(PackageSyncState.INIT);
                return;
            }

            // Check if there is a package known
            let packageId = undefined;
            if (localPackage?.id) {
                packageId = localPackage.id;
            } else {
                // Look for package_id in URL params
                packageId = packageIdParams.map((param) => searchParams.get(param)).find((id) => id !== null);
            }

            if (packageId) {
                setIsLoading(true);
                try {
                    const fetchedPackage = await PackageClient.fetchPackage(store.wizard.account_id, transaction.id, packageId);
                    setRemotePackage(fetchedPackage);

                    // Store fetched package if there are no local changes
                    if (fetchedPackage && !localPackage) {
                        dispatch(
                            updateData({
                                key: wizardId,
                                data: { package: fetchedPackage }
                            })
                        );
                        setPackageState(PackageSyncState.SYNCED);
                    } else {
                        // Compare fetched package with local wizard package
                        let hasLocalChanges = false;

                        if (localPackage) {
                            for (const key in localPackage) {
                                if (Object.prototype.hasOwnProperty.call(localPackage, key)) {
                                    const localValue = localPackage[key as keyof Package];
                                    const remoteValue = fetchedPackage[key as keyof Package];

                                    if (JSON.stringify(localValue) !== JSON.stringify(remoteValue)) {
                                        hasLocalChanges = true;
                                        break;
                                    }
                                }
                            }
                        }

                        if (hasLocalChanges) {
                            setPackageState(PackageSyncState.AHEAD);
                        } else {
                            setPackageState(PackageSyncState.SYNCED);
                        }
                    }
                } catch (error) {
                    Logger.error('Failed to fetch package:', error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setPackageState(PackageSyncState.INIT);
            }
        };

        initializePackage();
    }, [store, wizardId, autoFetch]);

    /**
     * Auto-create package if enabled
     */
    useEffect(() => {
        if (!autoCreate || !store || packageState !== PackageSyncState.INIT || isCreatingRef.current) return;

        const transaction = store.data?.transaction;
        if (!transaction?.id) return;

        let packageId = undefined;
        if (localPackage?.id) {
            packageId = localPackage.id;
        } else {
            // Look for package_id in URL params
            packageId = packageIdParams.map((param) => searchParams.get(param)).find((id) => id !== null);
        }

        if (packageId) return; // Already has package

        const shouldCreate = hasRequiredDataForCreation(store);
        if (shouldCreate) {
            isCreatingRef.current = true;
            syncPackage().finally(() => {
                isCreatingRef.current = false;
            });
        }
    }, [autoCreate, store, packageState]);

    /**
     * Cleanup on unmount
     */
    useEffect(() => {
        return () => {
            if (cleanupExecutedRef.current) return;
            cleanupExecutedRef.current = true;

            // Execute cleanup logic
            const cleanup = async () => {
                if (options.onReturn) {
                    const shouldProceed = await options.onReturn();
                    if (shouldProceed === false) return;
                }
            };

            cleanup();
        };
    }, [packageState, options.onReturn]);

    /**
     * Update package data locally (in Redux store only)
     */
    const localUpdatePackage = async (data: Partial<Package>, synced?: boolean) => {
        if (!store || !wizardId) {
            Logger.warn('Cannot update package: wizard store not available');
            return;
        }

        const newPackageData = {
            ...(localPackage || {}),
            ...data
        };

        // Update wizard store
        await dispatch(
            updateData({
                key: wizardId,
                data: {
                    package: newPackageData
                }
            })
        );

        // Mark as ahead if package exists on backend
        if (packageState === PackageSyncState.SYNCED && synced !== true) {
            setPackageState(PackageSyncState.AHEAD);
        }

        // Wait for the store to actually reflect the change
        const updated = await waitForStoreUpdate(() => {
            const currentStore = storeRef.current;
            const currentPackage = currentStore?.data?.package as Package | undefined;
            // Check if key properties are updated
            if (data.id && currentPackage?.id !== data.id) return false;
            if (data.product_id && currentPackage?.product_id !== data.product_id) return false;
            return true;
        });

        if (!updated) {
            Logger.warn('Store update timeout: package may not have been updated yet');
        }
    };

    const syncPackage = async (data?: Partial<Package>): Promise<Package> => {
        if (!store) {
            throw new Error('Cannot create or update package: wizard store not available');
        }

        const transaction = store.data?.transaction;
        if (!transaction?.id) {
            throw new Error('Cannot create or update package: transaction not available');
        }

        let packageData = remotePackage;

        if (!remotePackage) {
            // Create
            if (!hasRequiredDataForCreation(store)) {
                throw new Error('Cannot create package: missing required data (transaction)');
            }

            setIsLoading(true);
            try {
                const createData: CreatePackage = {
                    state: PackageStateName.PACKAGE_CREATED,
                    ...localPackage,
                    ...data
                } as CreatePackage;

                const newPackage = await PackageClient.postPackage(store.wizard.account_id, transaction.id, createData);

                packageData = newPackage;
                localUpdatePackage(newPackage, true);
                setRemotePackage(newPackage);
                setPackageState(PackageSyncState.SYNCED);

                // Call onCreate callback
                if (options.onCreate) {
                    await options.onCreate(newPackage);
                }
            } catch (error) {
                Logger.error('Failed to create package:', error);
                throw error;
            } finally {
                setIsLoading(false);
            }
        } else {
            // Update
            setIsLoading(true);
            try {
                const updateData: UpdatePackage = {
                    ...localPackage,
                    ...data
                } as UpdatePackage;

                const updatedPackage = await PackageClient.patchPackage(store.wizard.account_id, transaction.id, remotePackage.id, updateData);

                packageData = updatedPackage;
                localUpdatePackage(updatedPackage, true);
                setRemotePackage(updatedPackage);
                setPackageState(PackageSyncState.SYNCED);
            } catch (error) {
                Logger.error('Failed to update package:', error);
                throw error;
            } finally {
                setIsLoading(false);
            }
        }

        return packageData;
    };

    /**
     * Refresh package from backend
     */
    const refresh = async (): Promise<void> => {
        if (!store) {
            throw new Error('Cannot refresh package: wizard store not available');
        }
        const packageId = remotePackage?.id || localPackage?.id;
        if (!packageId) {
            throw new Error('Cannot refresh: no package ID');
        }

        const transactionId = store?.data?.transaction?.id || remotePackage?.transaction_id || localPackage?.transaction_id;
        if (!transactionId) {
            throw new Error('Cannot refresh: no transaction ID');
        }

        setIsLoading(true);
        try {
            const refreshedPackage = await PackageClient.fetchPackage(store.wizard.account_id, transactionId, packageId);
            if (refreshedPackage) {
                localUpdatePackage(refreshedPackage, true);
                setRemotePackage(refreshedPackage);
                setPackageState(PackageSyncState.SYNCED);
            }
        } catch (error) {
            Logger.error('Failed to refresh package:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const clearData = async () => {
        if (!store || !wizardId) {
            Logger.warn('Cannot clear package data: wizard store not available');
            return;
        }

        await dispatch(
            updateData({
                key: wizardId,
                data: {
                    package: undefined
                }
            })
        );
        setRemotePackage(null);
        setPackageState(PackageSyncState.INIT);
    };

    return {
        package: localPackage || null,
        remotePackage,
        isLoading,
        packageState,
        localUpdatePackage,
        syncPackage,
        refresh,
        clearData
    };
};
